import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { IDocumentTable } from '@models/Forms/IForms';
import { IFieldElem } from '@models/IFormData';
import { FormulaManager, hasUnsupportedRemoteFormulaData } from '@utils/managers/FormulaManager';
import { DocumentRuntimeContext } from '@/context/DocumentRuntimeContext';

/**
 * Режим листа согласования (`approvalListMode`): таблица показывается циклами
 * маршрута, а не списком строк. Правило приходит из конфигурации — обычно
 * константой `true`, но контракт допускает формулу, поэтому считается так же,
 * как остальные правила таблицы.
 */
export const useApprovalListMode = <TFieldValues extends object = object>(
    table: IDocumentTable,
    fields: Record<string, IFieldElem>,
    formMethods: UseFormReturn<TFieldValues>,
): boolean => {
    const runtime = useContext(DocumentRuntimeContext);
    const rules = table?.approvalListMode;
    const manager = useMemo(() => {
        if (!rules) return null;
        // в предпросмотре поддержки серверных источников формул может не быть
        if (runtime.mode === 'supportPreview' && hasUnsupportedRemoteFormulaData(rules, runtime.dataSource))
            return null;
        const created = new FormulaManager(rules, runtime.dataSource, runtime.requestCache);
        created.Init(fields, formMethods);
        return created;
    }, [rules, fields, formMethods, runtime.mode, runtime.dataSource, runtime.requestCache]);

    const [enabled, setEnabled] = useState(false);
    const activeRef = useRef(true);
    useEffect(() => {
        activeRef.current = true;
        return () => {
            activeRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (!manager) {
            setEnabled(false);
            return;
        }
        void manager
            .EvalFormulaValues(false, false)
            .then((value: boolean) => {
                if (activeRef.current) setEnabled(!!value);
            })
            .catch((error: unknown) => {
                console.error('Правило approvalListMode не посчиталось', error);
            });
    }, [manager]);

    return enabled;
};
